import { ImageButton } from '../../components/button/image.button';
import { CollectionTitle } from '../../components/collection/collection.title';
import { MaterialIcon } from '../../components/icon/icons';
import { Spinner } from '../../components/spinner/spinner';
import { T } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TopologicalEditorCollectionEditCtrl } from '../../page/topological.editor.page/topological.editor.collection.edit.ctrl';
import { Services } from '../../services';
import { CollectionEditState } from '../../state/collection.edit.state';
import { CurrentState } from '../../state/page.state';
import { PhotomeasureButton } from '../photomeasure.button/photomeasure.button';
import { CollectionEditCtrl } from './collection.view.ctrl';
import type { EditableCollectionController } from './collection.view.ctrl.interface';
import { CSS } from './collection.view.style';

export class CollectionEditViewTitle {
	static view(ctrl: EditableCollectionController) {
		let actions: MithrilVNode | MithrilVNode[];
		if (ctrl.isEditing) {
			actions = CollectionEditViewTitle.viewTitleEditActions(ctrl);
		} else {
			actions = CollectionEditViewTitle.viewTitleViewActions(ctrl);
		}

		// Create the default set of buttons
		const buttons = [
			CollectionEditViewTitle.viewButtonExpand(ctrl),
			CollectionEditViewTitle.viewButtonMetadata(ctrl),
			CollectionEditViewTitle.viewButtonZoomWindow(ctrl),
		];

		// If we are in the Topological Editor, then we need to add the carousel toggle button
		if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
			buttons.push(CollectionEditViewTitle.viewCarouselButton(ctrl));

			// If the carousel is the element that is showing, then we dont want to show the form expand & contract button as it is not needed
			if (ctrl.isCarouselShown) {
				buttons.shift();
			}
		}

		const collection = ctrl.collection;
		const title = collection.title || collection.subtitle;
		return m('div', { className: CSS.title.container }, [
			m('div', { className: CSS.title.tools }, actions),
			m(
				'a',
				{
					className: 'mdl-button ' + CSS.title.title,
					onclick: ctrl.zoomToCollection,
				},
				CollectionTitle.view(title)
			),
			m('div', { className: CSS.title.tools }, buttons),
		]);
	}

	static viewButtonExpand(ctrl: EditableCollectionController) {
		if (ctrl.isAllExpanded) {
			return ImageButton.view(MaterialIcon.KEY_DOWN, {
				onclick: ctrl.toggleExpandAll,
				title: T.COLLECTION_COLLAPSE_ALL,
			});
		}
		return ImageButton.view(MaterialIcon.KEY_UP, { onclick: ctrl.toggleExpandAll, title: T.COLLECTION_EXPAND_ALL });
	}

	static viewButtonMetadata(ctrl: EditableCollectionController) {
		if (ctrl.isMetadataShown) {
			return ImageButton.view(MaterialIcon.INFO, { onclick: ctrl.toggleMetaInfo, title: T.COLLECTION_SHOW_META });
		}
		return ImageButton.view(MaterialIcon.INFO_FILLED, {
			onclick: ctrl.toggleMetaInfo,
			title: T.COLLECTION_SHOW_META,
		});
	}

	static viewCarouselButton(ctrl: TopologicalEditorCollectionEditCtrl) {
		let title = ctrl.isCarouselShown ? T.TOPO_SHOW_COLLECTION_DATA : T.TOPO_SHOW_CAROUSEL;
		let icon = ctrl.isCarouselShown ? MaterialIcon.CONTENT_PASTE : MaterialIcon.FIELD_IMAGE;

		return ImageButton.view(icon, { onclick: ctrl.toggleCarousel, title, disabled: !ctrl.hasPhotos });
	}

	static viewButtonZoomWindow(ctrl: EditableCollectionController) {
		if (!ctrl.isPhotomeasure) {
			return null;
		}

		const icon = ctrl.isZoomShown ? MaterialIcon.GPS_ON : MaterialIcon.GPS_OFF;
		return ImageButton.view(icon, { onclick: ctrl.toggleZoomWindow, title: T.COLLECTION_SHOW_ZOOM });
	}

	static viewTitleEditActions(ctrl: EditableCollectionController): MithrilVNode | MithrilVNode[] {
		if (ctrl.isCollectionLoading || ctrl.isSaving) {
			return Spinner.view();
		}

		const userProfile = Services.UserProfile.get(Services.User.current.objectId);

		// if downloader or viewer the user, disable
		if (
			Services.UserProfile.isDepartmentDownloader(userProfile) ||
			Services.UserProfile.isDepartmentViewer(userProfile)
		) {
			return [
				ImageButton.view(MaterialIcon.CHECK, {
					className: CSS.title.save,
					title: T.EXPORT_MENU_TITLE_PERMISSION,
					disabled: true,
				}),
				ImageButton.view(MaterialIcon.CLOSE, {
					title: T.COLLECTION_EDIT_ABORT,
					onclick: ctrl.abortChanges,
				}),
			];
		}

		let editState: CollectionEditState | null = null;

		if (TopologicalEditorCollectionEditCtrl.is(ctrl)) {
			editState = ctrl.collectionEditState;
		} else if (CollectionEditCtrl.is(ctrl)) {
			editState = CurrentState.CollectionEdit;
		}

		// On the map page, dirty targets should enable save even if the
		// source collection's proxy state isn't dirty. The topo editor doesn't need this
		// because it manages multiple edit states simultaneously.
		const hasDirtyTargets =
			!TopologicalEditorCollectionEditCtrl.is(ctrl) && CurrentState.CollectionLink.hasDirtyTargets;
		const hasChanges = editState?.isDirty || hasDirtyTargets;

		return [
			ImageButton.view(MaterialIcon.CHECK, {
				className: CSS.title.save,
				title: hasChanges ? T.COLLECTION_EDIT_SAVE_CHANGES : T.COLLECTION_EDIT_NO_CHANGES,
				disabled: !hasChanges || !ctrl.isValid,
				onclick: ctrl.saveChanges,
			}),
			ImageButton.view(MaterialIcon.CLOSE, {
				className: CSS.title.discard,
				title: T.COLLECTION_EDIT_ABORT,
				disabled: !hasChanges || !ctrl.isValid,
				onclick: ctrl.abortChanges,
			}),
		];
	}

	static viewTitleViewActions(ctrl: EditableCollectionController): MithrilVNode {
		const collection = ctrl.collection;
		return m('div', { className: CSS.title.tools }, [
			PhotomeasureButton.view(collection.jobId, collection.uuid),
			ctrl.isEditable
				? ImageButton.view(MaterialIcon.EDIT, { onclick: ctrl.enterEditMode, title: T.COLLECTION_EDIT })
				: null,
		]);
	}
}
